import {ProvisionPaymentFrequency} from './provision-payment-frequency.enum';
import {provisionEnumLabelFallback} from './provision-enum-label-fallback';

/**
 * German presentation companion of {@link ProvisionPaymentFrequency} — the SINGLE place in the cp
 * frontend that NAMES a payment frequency, following the pattern of
 * `provision-account-kind-label.ts` and `provision-transaction-event-label.ts`.
 *
 * ── WHY IT LEFT THE .enum.ts
 *
 * The map and the option list used to sit in `provision-payment-frequency.enum.ts` beside the enum
 * itself. Every other enum of this module keeps its wire contract and its wording apart, and the
 * split is not cosmetic: the `.enum.ts` is a position-based MIRROR whose only job is to state the
 * ordinals the backend persists, and it is reviewed by putting it side by side with the Java file.
 * German prose in that file is noise during exactly that review, and it invites the opposite
 * mistake too — editing a caption in a file whose header says "never reorder, only append".
 *
 * ── THE LABELS ARE THE BACKEND'S OWN
 *
 * Typed out with real umlauts and identical to the `@UiEnumValueInfo(uiLabel = …)` of
 * `bm.enums.PaymentRecurringAmountFrequency`, so a frequency reads the same here as on every bm
 * surface. Keyed by the enum MEMBER, never by an ordinal literal: a map written with numeric keys
 * would silently survive a backend reordering, which is the failure mode 02-datenmodell.md §0.5
 * exists to prevent.
 */
export const CP_PAYMENT_FREQUENCY_LABELS: Readonly<Record<ProvisionPaymentFrequency, string>> = {
    [ProvisionPaymentFrequency.MONTHLY]: 'Monatlich',
    [ProvisionPaymentFrequency.QUARTERLY]: 'Vierteljährlich',
    [ProvisionPaymentFrequency.SEMI_ANNUALLY]: 'Halbjährlich',
    [ProvisionPaymentFrequency.YEARLY]: 'Jährlich'
};

/** German label of a payment frequency; an unmapped constant stays VISIBLE, never blank. */
export function getProvisionPaymentFrequencyLabel(frequency: ProvisionPaymentFrequency): string {
    return CP_PAYMENT_FREQUENCY_LABELS[frequency]
        ?? provisionEnumLabelFallback(ProvisionPaymentFrequency, frequency);
}

/** One entry of {@link PROVISION_PAYMENT_FREQUENCY_OPTIONS}. */
export interface ProvisionPaymentFrequencyOption {
    label: string;
    value: ProvisionPaymentFrequency;
}

/**
 * The four frequencies as the value list of the simulation form's select field. Built FROM
 * {@link CP_PAYMENT_FREQUENCY_LABELS}, so an option can never disagree with a caption rendered
 * elsewhere. Declaration order is display order; MONTHLY comes first because it is the default of
 * the form and the frequency 87 % of the productive stock runs on.
 */
export const PROVISION_PAYMENT_FREQUENCY_OPTIONS: ProvisionPaymentFrequencyOption[] = [
    {label: CP_PAYMENT_FREQUENCY_LABELS[ProvisionPaymentFrequency.MONTHLY], value: ProvisionPaymentFrequency.MONTHLY},
    {label: CP_PAYMENT_FREQUENCY_LABELS[ProvisionPaymentFrequency.QUARTERLY], value: ProvisionPaymentFrequency.QUARTERLY},
    {label: CP_PAYMENT_FREQUENCY_LABELS[ProvisionPaymentFrequency.SEMI_ANNUALLY], value: ProvisionPaymentFrequency.SEMI_ANNUALLY},
    {label: CP_PAYMENT_FREQUENCY_LABELS[ProvisionPaymentFrequency.YEARLY], value: ProvisionPaymentFrequency.YEARLY}
];
